import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, shareReplay, throwError } from 'rxjs';
import { AppSettings } from '../app-settings';
import { AuthenticationService } from './authentication.service';

@Injectable({
  providedIn: 'root'
})
export class PlacesService {

  private placeObject = new BehaviorSubject<any>(null);
  public placeObservable = this.placeObject.asObservable();

  private mapAction = new BehaviorSubject<any>(null);
  public mapActionObservable = this.mapAction.asObservable();

  // Save Map
  private mapSaveAction = new BehaviorSubject<any>(null);
  public mapSaveObservable = this.mapSaveAction.asObservable();

  private inFlightRequests = new Map<number, Observable<any>>();

  constructor(private http: HttpClient, private auth: AuthenticationService) { }

  /**
   * Deja en una lista de espacios de la compañía solo los que el usuario tiene
   * asignados.
   *
   * Los selectores del panel se llenan con `/spaces?id_company=...`, que es
   * público y devuelve todos los recintos de la empresa. La API ya acota los
   * datos al espacio asignado (App\Services\SpaceAccess en el back), así que
   * ofrecer un recinto ajeno solo consigue un listado vacío o un 403 al
   * guardar. Un superadmin los ve todos.
   *
   * Si el perfil todavía no trae los espacios no se filtra: quien manda sobre
   * los datos es el back, esto es únicamente para no ofrecer opciones inútiles.
   */
  filterManagedPlaces(spaces: any[], idBusiness: any): any[] {
    if (!Array.isArray(spaces)) return [];

    const profile = this.auth.getProfile();
    const isSuperAdmin = (profile?.roles || []).some((role: any) => role.name === 'superadmin');
    if (isSuperAdmin) return spaces;

    const allowedIds = (profile?.companies || [])
      .filter((company: any) => !idBusiness || company.id == idBusiness)
      .flatMap((company: any) => company.spaces || [])
      .map((space: any) => space.id);

    if (!allowedIds.length) return spaces;

    return spaces.filter((space: any) => allowedIds.includes(space.id));
  }

  saveMapAction(save: any) {
    this.mapSaveAction.next(save);
  }
  
  getPlaceSaved() {
    this.placeObject.getValue();
  }

  setActionMap(action: any) {
    this.mapAction.next(action);
  }

  getListFilterPlaces(idBusiness: number, page: number, searchText: any, sortFilter: any, all: boolean = false) {
    let url = `${AppSettings.API_URI_V2}/spaces?id_company=${idBusiness}`;
    if (searchText) {
      url += `&search=${searchText}`;
    }
    if (sortFilter) {
      url += `&orderBy=${sortFilter.sortedBy}&order=${sortFilter.sort}`;
    }
    if (all) {
      url += `&all=true`;
    } else {
      url += `&page=${page}`;
    }
    return this.http.get<any>(url).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  getPlaceById(idVenue: number): Observable<any> {
    const id = +idVenue;
    if (this.inFlightRequests.has(id)) {
      return this.inFlightRequests.get(id)!;
    }
    const url = `${AppSettings.API_URI_V2}/spaces/find/${id}`;
    const request$ = this.http.get<any>(url).pipe(
      map((data) => {
        this.placeObject.next(data.data);
        this.inFlightRequests.delete(id);
        return data;
      }),
      catchError(err => {
        this.inFlightRequests.delete(id);
        return throwError(() => err);
      }),
      shareReplay(1)
    );
    this.inFlightRequests.set(id, request$);
    return request$;
  }

  clearPlaceCache() {
    this.inFlightRequests.clear();
  }

  getPurchasesSpaces(business: number, page: number, space: number, searchText: string, sortFilter: any) {
    let url = `${AppSettings.API_URI_V2}/companies/${business}/orders/count?page=${page}`;
    if (space && space > 0) {
      url += `&id_space=${space}`;
    }
    if (searchText) {
      url += `&search=${searchText}`;
    }
    if (sortFilter) {
      url += `&sort=${sortFilter.sortedBy}:${sortFilter.sort}`;
    }
    return this.http.get<any>(url).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  updateImage(venueId: number, payload: any) {
    const url = `${AppSettings.API_URI_V2}/spaces/${venueId}`;
    return this.http.put<any>(url, payload).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  //V2
  createVenue(payload: any) {
    const url = `${AppSettings.API_URI_V2}/spaces`;
    return this.http.post<any>(url, payload).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  getPlacesByCompany(businessId: any) {
    const url = `${AppSettings.API_URI_V2}/spaces?id_company=${businessId}`;
    return this.http.get<any>(url).pipe(
      map((res) => {
        // Mapea los espacios con location y mantiene la estructura de respuesta
        const mappedSpaces = res.data.data.map((space: any) => ({
          ...space,
          location: space.city?.name || space.address || ''
        }));
        return {
          ...res,
          data: {
            ...res.data,
            data: mappedSpaces
          }
        };
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  getPlacesByUser(userId: any) {
    const url = `${AppSettings.API_URI_V2}/spaces?id_user=${userId}`;
    return this.http.get<any>(url).pipe(
      map((data) => {
        return data;
      }),
      catchError(err => { return throwError(() => err); })
    );
  }
  
  getFilteredSpaces(idBusiness: number, page: number, searchText: any, sortFilter: any){
    let url = `${AppSettings.API_URI_V2}/spaces?id_company=${idBusiness}&page=${page}`;
    if (searchText) {
      url += `&search=${searchText}`;
    }
    if (sortFilter) {
      url += `&orderBy=${sortFilter.sortedBy}&order=${sortFilter.sort}`;
    }
    return this.http.get<any>(url).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  updateVenue(venueId: number, payload: any) {
    const url = `${AppSettings.API_URI_V2}/spaces/${venueId}`;
    return this.http.put<any>(url, payload).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  getInfoSpace(slug: string) {
    const url = `${AppSettings.API_URI_V2}/spaces/${slug}`;
    return this.http.get<any>(url).pipe(
      map((data) => {
        return data
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

}
